import {
    faCog,
    faExclamationTriangle,
    faLifeRing,
    faPencil,
    faTrash,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIconProps } from "@fortawesome/react-fontawesome";
import { Location } from "history";
import isNumber from "lodash-es/isNumber";
import { parse } from "query-string";
import React from "react";
import { MapStateToPropsParam } from "react-redux";

import { analyticsStoreEvent } from "@/mk2/actions";
import { BlogPostFormData } from "@/mk2/apps/blogs/components/BlogPostCreateForm";
import { BoxedHashtags } from "@/mk2/apps/blogs/components/HashtagsBox";
import LeftMenu from "@/mk2/apps/blogs/components/LeftMenu";
import {
    PrefillData,
    advancedArticleCreateEditSetPrefillData,
} from "@/mk2/apps/blogs/containers/AdvancedArticleCreateEdit/AdvancedArticleCreateEdit.actions";
import commentsSaga from "@/mk2/apps/blogs/containers/Comments/Comments.sagas";
import { BLOG_POST_MENU_MORE } from "@/mk2/apps/blogs/helpers";
import {
    BrandDaysCampaignEntity,
    BrandDaysCampaignSchema,
    PageRef,
    PostEntity,
    PostSchema,
} from "@/mk2/apps/forum/schemas";
import { parsePageNumber } from "@/mk2/apps/forum/utils";
import { emptyComplaintsCounts } from "@/mk2/apps/groups/containers/GroupReportedFeed/GroupReportedFeed.constants";
import {
    groupSharedDeleteGroupPostInit,
    groupSharedFeedRead,
    groupSharedInviteUser,
    groupSharedJoinGroup,
    groupSharedLeaveGroup,
    groupSharedPostSticky,
    groupSharedSetGroupAsFavorite,
    groupSharedSubscribeGroup,
} from "@/mk2/apps/groups/containers/GroupShared/GroupShared.actions";
import groupSharedReducer from "@/mk2/apps/groups/containers/GroupShared/GroupShared.reducers";
import groupSharedSaga from "@/mk2/apps/groups/containers/GroupShared/GroupShared.sagas";
import {
    getCommentComplaintsCounts,
    getGroupSharedState,
    getModeratorUsersByGroupId,
    getPostsCommentsCount,
} from "@/mk2/apps/groups/containers/GroupShared/GroupShared.selectors";
import { groupsGroupEditUrl, groupsGroupReportedUrl } from "@/mk2/apps/groups/urls";
import { BrandDaysPosts } from "@/mk2/apps/home/components/BrandDaysPosts";
import { Btn, BtnType } from "@/mk2/components/Btn";
import { Count } from "@/mk2/components/NotificationsBadge";
import { MenuItem } from "@/mk2/components/menu/MenuItem";
import { AppName } from "@/mk2/containers/Application/Application";
import { complaintOpenTrigger } from "@/mk2/containers/Complaint/Complaint.actions";
import { ComplaintTargetType } from "@/mk2/containers/Complaint/Complaint.schemas";
import { getComplaintsShownForBlogPosts } from "@/mk2/containers/Complaints/Complaints.selectors";
import { Menu } from "@/mk2/containers/Menu/Menu";
import { menuToggle } from "@/mk2/containers/Menu/Menu.actions";
import { cacheLast, memoizeOne } from "@/mk2/helpers/cache";
import { UserAgentParsed } from "@/mk2/helpers/detects";
import { MapDispatchToPropsObject } from "@/mk2/helpers/types";
import { removeEmpty, url } from "@/mk2/helpers/urls";
import Page, {
    AnchorLeaderboardProps,
    BrandingProps,
    HalfpageProps,
    InterstitialProps,
    PageRouteProps,
    RightContentSide,
} from "@/mk2/pages/Page";
import {
    AppState,
    getHasRecipesAccess,
    getRequestBaseUrl,
    getRequestDeviceIsIOS,
    getRequestDeviceMobile,
    getRequestPermissions,
    getRequestUser,
    getRequestUserAgent,
} from "@/mk2/reducers";
import { LoadingState } from "@/mk2/reducers/loadingState";
import {
    BlogPostEntity,
    BlogPostSchema,
    EshopProductEntity,
    EshopProductSchema,
    GroupEntity,
    GroupMemberEntity,
    GroupMemberSchema,
    GroupSchema,
    MenuSelection,
    UserEntity,
    UsersByGroupId,
    WikiArticleEntity,
    WikiArticleSchema,
} from "@/mk2/schemas";
import { getDenormalizedEntities, getDenormalizedEntity } from "@/mk2/selectors";
import { Store } from "@/mk2/store/configureStore";
import {
    ADMINCLUB_MENU,
    DELETE,
    GROUP_EDIT,
    GROUP_REPORTED_POSTS,
} from "@/mk/autogenerated/translation";
import { hasAds, isAuthenticated, isAuthenticatedAs } from "@/mk/bazaar/common/userUtils";
import { adminclub_group_url } from "@/mk/urls/functions";

import { NO_BRANDING_AFTER } from "../../components/GroupFeedPosts";
import GroupFeed, { SAME_DATE_GROUP_CATEGORY_ID } from "./GroupFeed";
import {
    groupDeleteGroup,
    groupFeedFetchTrigger,
    groupFeedScrollToTrigger,
    groupGuideCompleteTrigger,
    groupPostCreateTrigger,
} from "./GroupFeed.actions";
import { GROUP_FEED_MENU_MORE, GROUP_FEED_MENU_ORDERBY } from "./GroupFeed.constants";
import reducer, {
    GroupFeedPageState,
    GroupFeedVirtualPageState,
    initialGroupFeedPageState,
} from "./GroupFeed.reducers";
import saga from "./GroupFeed.sagas";
import {
    getGroupFeedPageState,
    getGroupFeedSearchState,
    getGroupFeedState,
    getGroupFeedVirtualPageState,
    getGroupLastVisitedCount,
} from "./GroupFeed.selectors";
import styles from "./GroupFeedPage.mscss";

const fasCogLg: FontAwesomeIconProps = { icon: faCog, size: "lg" };

export enum ScrollToPosition {
    FirstPost = "first",
    LastPost = "last",
}

export type OrderBy = "comments" | "posts";

interface OwnProps {}

interface StateProps {
    askedForMembership: number[];
    baseUrl: string;
    commentsCounts: { [postId: number]: number };
    isCommented: boolean;
    complaintsCounts: { [postId: number]: number };
    complaintsOpened: boolean[];
    count: number;
    errorMessage: string;
    group: GroupEntity;
    groupId: number; // extracted from 'match.params.groupId'
    neighbourGroups: GroupEntity[];
    hasComplaints: boolean;
    hashtags: BoxedHashtags;
    initialValues: BlogPostFormData;
    isBlocked: boolean;
    isFavorite: boolean;
    isIOS: boolean;
    isMember: boolean;
    isModerator: boolean;
    isSubscribed: boolean;
    isSubscribing: boolean;
    joining: number[];
    lastVisitedCount: Count;
    loadingState: LoadingState;
    memberships: number[];
    isMobile: boolean;
    moderators: GroupMemberEntity[];
    moderatorUsersByGroupId: UsersByGroupId;
    numberOfPages: number;
    orderBy: OrderBy;
    pageRef: PageRef;
    permissions: string[];
    posts: BlogPostEntity[];
    feedLoaded: boolean;
    isPosted: boolean;
    query: string;
    requestUser: UserEntity;
    userAgent: UserAgentParsed;
    activeBrandDaysCampaign: BrandDaysCampaignEntity;
    activeBrandDaysPosts: PostEntity[];
    countyId: number;
    productsCount: number;
    isClosed: boolean;
    isAdmin: boolean;
    hasRecipesAccess: boolean;

    eshopProductsHeader: EshopProductEntity[];
    eshopProducts: EshopProductEntity[];
    eshopProductsFooter: EshopProductEntity[];

    promoReviewArticlesHeader: WikiArticleEntity[];
    promoReviewArticlesMiddle: WikiArticleEntity[];
    promoReviewArticlesFooter: WikiArticleEntity[];
}

interface DispatchProps {
    onAddPost(groupId: number, pageRef: PageRef, values: BlogPostFormData);
    onChangeSticky(postId: number, sticky: boolean);
    onDelete(postId: number);
    onFeedRead(groupId: number);
    onGroupAddToFavorites(groupId: number, userId: number);
    onGroupDelete(groupId: number);
    onGroupFeedMenuOrderBy();
    onGroupJoin(groupId: number, origin: string);
    onGroupLeave(groupId: number);
    onGroupSubscribe(groupId: number, username: string);
    onGroupRemoveFromFavorites(groupId: number, userId: number);
    onGroupToggleMenuMore();
    onGroupUnsubscribe(groupId: number, username: string);
    onInviteUser(groupId: number);
    onLoad(
        groupId: number,
        pageRef: PageRef,
        orderBy: OrderBy,
        query: string,
        currentUrl: Location,
        numCommentsBeforeLoad: number,
    );
    onPostToggleMenuMore(postId: number);
    onReport(entity: ComplaintTargetType, id: number);
    onScrollTo(scrollto: ScrollToPosition);
    onSetPrefillData(data: PrefillData);
    onStoreEvent(name: string, props: any);
    onGuideComplete(groupId: number, isClosedByUser: boolean);
}

type RouteProps = PageRouteProps<{
    groupId: string;
}>;

export type Props = OwnProps & StateProps & DispatchProps & RouteProps;

interface Query {
    orderby: OrderBy;
    query: string;
}

const initialValues = {
    perex: "",
    photos: [],
    sticker: null,
};

export default class GroupFeedPage extends Page<OwnProps & RouteProps, StateProps, DispatchProps> {
    private onDeleteCache = cacheLast();

    private parseQuery = memoizeOne((qs: string): Query => {
        const qp = removeEmpty(parse(qs) || {});
        return {
            // empty string value is considered correct too and needs to be preserved as such
            orderby: (qp.orderby as OrderBy) || "posts",
            query: qp.query !== undefined ? (qp.query as string) : null,
        };
    });

    public mapStateToProps(): MapStateToPropsParam<StateProps, OwnProps & RouteProps, AppState> {
        return (state: AppState, ownProps: OwnProps & RouteProps): StateProps => {
            const {
                match: { params },
            } = ownProps;
            const groupId = parseInt(params.groupId, 10);
            const parsedQuery: Query = this.parseQuery(ownProps.location.search);
            const query = parsedQuery.query;
            const orderBy = parsedQuery.orderby;

            const pageRef = parsePageNumber(ownProps.location);

            const groupSharedState = getGroupSharedState(state);
            const groupFeedState = getGroupFeedState(state, groupId);
            const groupPostsState = getGroupFeedSearchState(state, groupId, orderBy, query);

            const group = getDenormalizedEntity<GroupEntity>(state, GroupSchema, groupId);
            const moderators = getDenormalizedEntities<GroupMemberEntity>(
                state,
                GroupMemberSchema,
                groupFeedState.moderators,
            );
            const moderatorUsers = moderators.map((groupMember) => groupMember.user);

            const permissions = getRequestPermissions(state);
            const requestUser = getRequestUser(state);
            const userAgent = getRequestUserAgent(state);

            const isAdmin = permissions.indexOf("forum.can_manage_forum") >= 0;
            const isModerator = moderatorUsers && isAuthenticatedAs(requestUser, moderatorUsers);

            const groupFeedPageState: GroupFeedPageState | GroupFeedVirtualPageState = isNumber(
                pageRef,
            )
                ? getGroupFeedPageState(state, groupId, orderBy, query, pageRef)
                : getGroupFeedVirtualPageState(state, groupId, orderBy, query, pageRef);

            const postIds = isNumber(pageRef)
                ? (groupFeedPageState as GroupFeedPageState).postIds
                : initialGroupFeedPageState.postIds;
            const posts = getDenormalizedEntities<BlogPostEntity>(state, BlogPostSchema, postIds);
            const feedLoaded = postIds !== initialGroupFeedPageState.postIds;

            const isPosted = groupFeedState.userPosts
                ? groupFeedState.userPosts
                : posts.filter((p) => p.user.id === requestUser.id && p.deletedAt === null).length >
                  0;

            const userCommentCount = groupFeedState.commentCount;

            let lastCreatedComments = groupFeedState.lastCreatedComments.length;
            let lastDeletedComments = groupFeedState.lastDeletedComments.length;

            if (lastCreatedComments === undefined) {
                lastCreatedComments = 0;
            }
            if (lastDeletedComments === undefined) {
                lastDeletedComments = 0;
            }
            const num = userCommentCount + lastCreatedComments - lastDeletedComments;

            const isCommented = num > 0;

            const isClosed = groupFeedState.isClosed;

            const memberships = groupSharedState.memberships;

            const commentsLoadingState = groupFeedState.commentsLoadingState;

            const commentsCounts = getPostsCommentsCount(state, posts);

            const complaintsCounts =
                isAdmin || isModerator
                    ? getCommentComplaintsCounts(state, postIds, commentsLoadingState)
                    : emptyComplaintsCounts;

            const lastVisitedCount = getGroupLastVisitedCount(state, groupId);

            return {
                askedForMembership: groupSharedState.askedForMembership,
                baseUrl: getRequestBaseUrl(state),
                commentsCounts,
                isCommented,

                eshopProductsHeader: getDenormalizedEntities<EshopProductEntity>(
                    state,
                    EshopProductSchema,
                    (groupFeedPageState as GroupFeedPageState).eshopProductsHeaderIds,
                ),
                eshopProducts: getDenormalizedEntities<EshopProductEntity>(
                    state,
                    EshopProductSchema,
                    (groupFeedPageState as GroupFeedPageState).eshopProductsIds,
                ),
                eshopProductsFooter: getDenormalizedEntities<EshopProductEntity>(
                    state,
                    EshopProductSchema,
                    (groupFeedPageState as GroupFeedPageState).eshopProductsFooterIds,
                ),

                promoReviewArticlesHeader: getDenormalizedEntities<WikiArticleEntity>(
                    state,
                    WikiArticleSchema,
                    (groupFeedPageState as GroupFeedPageState).promoReviewArticlesHeaderIds,
                ),
                promoReviewArticlesMiddle: getDenormalizedEntities<WikiArticleEntity>(
                    state,
                    WikiArticleSchema,
                    (groupFeedPageState as GroupFeedPageState).promoReviewArticlesMiddleIds,
                ),
                promoReviewArticlesFooter: getDenormalizedEntities<WikiArticleEntity>(
                    state,
                    WikiArticleSchema,
                    (groupFeedPageState as GroupFeedPageState).promoReviewArticlesFooterIds,
                ),

                complaintsCounts,
                complaintsOpened: getComplaintsShownForBlogPosts(state, posts),
                count: groupPostsState.count,
                errorMessage: groupFeedPageState.errorMessage,
                group,
                groupId,
                neighbourGroups: getDenormalizedEntities<GroupEntity>(
                    state,
                    GroupSchema,
                    groupFeedState.neighbourGroups,
                ),
                hasComplaints: groupFeedState.hasComplaints,
                hashtags: groupFeedState.hashtags,
                initialValues,
                isBlocked: groupFeedState.isBlocked,
                isFavorite: groupSharedState.favorites.includes(groupId),
                isIOS: getRequestDeviceIsIOS(state),
                isMember: memberships.includes(groupId),
                isModerator,
                isSubscribed: groupFeedState.isSubscribed,
                isSubscribing: groupFeedState.isSubscribing,
                joining: groupSharedState.joining,
                lastVisitedCount,
                loadingState: groupFeedPageState.loadingState,
                memberships,
                isMobile: getRequestDeviceMobile(state),
                moderators,
                moderatorUsersByGroupId: getModeratorUsersByGroupId(moderators),
                numberOfPages: groupPostsState.numberOfPages,
                orderBy,
                pageRef,
                permissions,
                posts,
                feedLoaded,
                isPosted,
                query,
                requestUser,
                userAgent,
                isAdmin,
                activeBrandDaysCampaign: getDenormalizedEntity<BrandDaysCampaignEntity>(
                    state,
                    BrandDaysCampaignSchema,
                    groupFeedState.activeBrandDaysCampaignId,
                ),
                activeBrandDaysPosts: getDenormalizedEntities<PostEntity>(
                    state,
                    PostSchema,
                    groupFeedState.activeBrandDaysPostsIds,
                ),
                countyId: groupFeedState.countyId,
                productsCount: groupFeedState.productsCount,
                isClosed,
                hasRecipesAccess: getHasRecipesAccess(state),
            };
        };
    }

    public mapDispatchToProps(): MapDispatchToPropsObject<DispatchProps> {
        return {
            onAddPost: groupPostCreateTrigger,
            onChangeSticky: groupSharedPostSticky,
            onDelete: groupSharedDeleteGroupPostInit,
            onFeedRead: groupSharedFeedRead,
            onGroupAddToFavorites: (groupId, userId) =>
                groupSharedSetGroupAsFavorite(groupId, userId, true),
            onGroupDelete: groupDeleteGroup,
            onGroupFeedMenuOrderBy: () => menuToggle(GROUP_FEED_MENU_ORDERBY),
            onGroupJoin: groupSharedJoinGroup,
            onGroupLeave: (groupId) => groupSharedLeaveGroup(groupId, true),
            onGroupSubscribe: (groupId, username) =>
                groupSharedSubscribeGroup(groupId, username, true),
            onGroupRemoveFromFavorites: (groupId, userId) =>
                groupSharedSetGroupAsFavorite(groupId, userId, false),
            onGroupToggleMenuMore: () => menuToggle(GROUP_FEED_MENU_MORE),
            onGroupUnsubscribe: (groupId, username) =>
                groupSharedSubscribeGroup(groupId, username, false),
            onInviteUser: groupSharedInviteUser,
            onLoad: groupFeedFetchTrigger,
            onPostToggleMenuMore: (postId: number) => menuToggle(BLOG_POST_MENU_MORE(postId)),
            onReport: complaintOpenTrigger,
            onScrollTo: groupFeedScrollToTrigger,
            onSetPrefillData: advancedArticleCreateEditSetPrefillData,
            onStoreEvent: analyticsStoreEvent,
            onGuideComplete: (groupId, isClosedByUser) =>
                groupGuideCompleteTrigger(groupId, isClosedByUser),
        };
    }

    public getMetaTitle(props: Props): string {
        const { group } = props;
        return group ? group.name : null;
    }

    public getMetaDescription(props: Props): string {
        const { group } = props;
        return group ? group.description : null;
    }

    public render(props: Props): false | JSX.Element {
        return <GroupFeed key={props.query} {...props} />;
    }

    public renderRight(props: Props): JSX.Element {
        const { activeBrandDaysCampaign, activeBrandDaysPosts, location, requestUser } = props;

        const showBrandDays =
            !!activeBrandDaysCampaign && activeBrandDaysPosts.length > 0 && hasAds(requestUser);
        return (
            <>
                {showBrandDays && (
                    <BrandDaysPosts
                        brandDaysCampaign={activeBrandDaysCampaign}
                        brandDaysPosts={activeBrandDaysPosts}
                    />
                )}
                <LeftMenu location={location} />
            </>
        );
    }

    public getRightContentSide(app: AppName, props: Props): RightContentSide {
        return RightContentSide.LeftSide;
    }

    public hasHeaderBackButton(props: Props): boolean {
        return false;
    }

    public getHalfpageProps(props: Props): HalfpageProps {
        const { requestUser, group } = props;

        if (
            (group && group.isNoAds) ||
            (group &&
                group.categoryId === SAME_DATE_GROUP_CATEGORY_ID &&
                group.domainMonth >= NO_BRANDING_AFTER)
        ) {
            return null;
        }

        // AdX flaguje skupiny s kratkym popisom ako "Valuable Inventory: No content"
        // tak neprihlasenym zobrazujeme reklamu, len ak je popis skupiny dostatocne dlhy
        if (
            isAuthenticated(requestUser) ||
            (group && group.description && group.description.length > 300)
        ) {
            return {
                zone: "Groups Feed - Halfpage Sticky (Desktop)",
            };
        }
    }

    public getBrandingProps(props: Props): BrandingProps {
        const { requestUser, group } = props;

        if (
            (group && group.isNoAds) ||
            (group &&
                group.categoryId === SAME_DATE_GROUP_CATEGORY_ID &&
                group.domainMonth >= NO_BRANDING_AFTER)
        ) {
            return null;
        }

        // AdX flaguje skupiny s kratkym popisom ako "Valuable Inventory: No content"
        // tak neprihlasenym zobrazujeme reklamu, len ak je popis skupiny dostatocne dlhy
        if (
            isAuthenticated(requestUser) ||
            (group && group.description && group.description.length > 300)
        ) {
            return {
                zone: props.isMobile ? "Groups Feed - Top 01 - Mobile" : "Groups - Branding",
            };
        }
    }

    public getInterstitialProps(props: Props): InterstitialProps | null {
        return { zone: props.isMobile ? "Groups Feed - Interstitial" : "Groups - Interstitial" };
    }

    public getAnchorLeaderboardProps(props: Props): AnchorLeaderboardProps {
        const { requestUser, isMobile, group } = props;

        if (!isMobile || (requestUser && !hasAds(requestUser))) {
            return null;
        }

        if (
            !group ||
            (group.categoryId === SAME_DATE_GROUP_CATEGORY_ID &&
                group.domainMonth >= NO_BRANDING_AFTER)
        ) {
            return null;
        }

        if (!isAuthenticated(requestUser) && (group.description.length || 0) <= 300) {
            return null;
        }

        return {
            zone: "Groups Feed - Anchor - Mobile",
        };
    }

    public hasMobileLogo(props: Props): boolean {
        return true;
    }

    public getHeaderRight(props: Props): React.ReactElement {
        const {
            group,
            hasComplaints,
            isModerator,
            permissions,
            onGroupDelete,
            onGroupToggleMenuMore,
        } = props;

        const isDeleted = group && group.deletedAt;
        if (isDeleted) {
            return null;
        }

        const isAdmin = permissions.indexOf("forum.can_manage_forum") >= 0;

        const role = isAdmin ? "admin" : isModerator ? "moderator" : "";

        if (isModerator || isAdmin) {
            const hasEdit = (group && isAdmin) || isModerator;
            const hasDelete = group && isAdmin && false; // TBD delete is actually disabled
            const hasAdminclub = group && isAdmin;

            return hasEdit || hasComplaints || hasDelete || hasAdminclub ? (
                <Menu
                    key="menu"
                    menuId={GROUP_FEED_MENU_MORE}
                    trigger={
                        <Btn
                            type={BtnType.BlueOutline}
                            icon={fasCogLg}
                            onClick={onGroupToggleMenuMore}
                            bullet={hasComplaints}
                            bulletClassName={styles.GroupFeedPage__menu__trigger__bullet}
                        />
                    }
                >
                    {hasComplaints && (
                        <MenuItem
                            label={GROUP_REPORTED_POSTS}
                            icon={faExclamationTriangle}
                            className={styles[`GroupFeedPage__menu__item--${role}`]}
                            link={url(groupsGroupReportedUrl, { groupId: group.id })}
                            isAdminUI={!isModerator && isAdmin}
                        />
                    )}
                    {hasEdit && (
                        <MenuItem
                            label={GROUP_EDIT}
                            icon={faPencil}
                            link={url(groupsGroupEditUrl, { groupId: group.id })}
                            isAdminUI={!isModerator && isAdmin}
                        />
                    )}
                    {hasDelete && (
                        <MenuItem
                            label={DELETE}
                            icon={faTrash}
                            onClick={this.onDeleteCache(() => {
                                onGroupDelete(group.id);
                            }, group.id)}
                            isAdminUI
                        />
                    )}
                    {hasAdminclub && (
                        <MenuItem
                            label={ADMINCLUB_MENU}
                            icon={faLifeRing}
                            link={adminclub_group_url(group.id)}
                            asAnchor
                            isAdminUI
                        />
                    )}
                </Menu>
            ) : null;
        } else {
            return null;
        }
    }

    public getSubmitButton(props: Props): React.ReactElement {
        return null;
    }

    public getHeaderMenuSelection(app: AppName, props: Props): MenuSelection {
        return {
            activeSection: "groups",
            activeItem: "feed",
        };
    }

    public registerInStore(store: Store<AppState>) {
        store.injectReducer("containers.groups.groupFeed", reducer);
        store.injectSaga("containers.groups.groupFeed", saga);

        store.injectReducer("containers.groups.groupShared", groupSharedReducer);
        store.injectSaga("containers.groups.groupShared", groupSharedSaga);

        store.injectSaga("containers.blogs.shared.comments", commentsSaga);
    }
}
